/**
 * Визард «Создать тип записи из примера Jira-задач».
 *
 * Шаг 1 — пользователь вводит 1..20 ключей задач (по строке) и жмёт «Получить
 * превью». Бэкенд возвращает draft (поля + предложенные справочники).
 *
 * Шаг 2 — пользователь редактирует draft:
 *   - имя/slug типа,
 *   - имена предложенных справочников и (опционально) ключевые колонки,
 *   - имена/slug полей, тип поля (для совместимых преобразований), флажки
 *     skip/title.
 * Затем «Создать» — отправляем draft в /commit, бэкенд транзакционно создаёт
 * ItemType + поля + справочники + JiraSyncConfig.
 *
 * Маппинг полей в Jira (push) уже подготовлен в draft и сразу попадёт в
 * созданный JiraSyncConfig, поэтому после визарда тип готов к push'у.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  DraftField,
  DraftFieldKind,
  ItemTypeFromJiraSampleDraft,
  ProposedDictionary,
} from '@lens/shared';
import {
  commitItemTypeFromJiraSample,
  previewItemTypeFromJiraSample,
} from '../jira/api.ts';

const { Text } = Typography;
const { TextArea } = Input;

interface Props {
  spaceId: string;
  open: boolean;
  onCancel: () => void;
  onCreated: (itemTypeId: string) => void;
}

const FIELD_KIND_OPTIONS: Array<{ value: DraftFieldKind; label: string }> = [
  { value: 'text', label: 'Текст' },
  { value: 'textarea', label: 'Многострочный текст' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'number', label: 'Число' },
  { value: 'date', label: 'Дата' },
  { value: 'select', label: 'Список (один)' },
  { value: 'multi_select', label: 'Список (несколько)' },
  { value: 'reference', label: 'Справочник' },
  { value: 'url', label: 'URL' },
  { value: 'boolean', label: 'Да/нет' },
  { value: 'status', label: 'Статус' },
  { value: 'skip', label: '— не импортировать —' },
];

export function CreateItemTypeFromJiraWizard({
  spaceId,
  open,
  onCancel,
  onCreated,
}: Props): JSX.Element {
  const [step, setStep] = useState<'input' | 'edit'>('input');
  const [issueKeysText, setIssueKeysText] = useState('');
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<ItemTypeFromJiraSampleDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep('input');
    setIssueKeysText('');
    setDraft(null);
    setError(null);
    setLoading(false);
  }, []);

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  const handlePreview = useCallback(async () => {
    const keys = issueKeysText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (keys.length === 0) {
      setError('Введите хотя бы один ключ задачи Jira');
      return;
    }
    if (keys.length > 20) {
      setError('Не более 20 задач за раз');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await previewItemTypeFromJiraSample(spaceId, keys);
      setDraft(result);
      setStep('edit');
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось получить превью');
    } finally {
      setLoading(false);
    }
  }, [issueKeysText, spaceId]);

  const handleCommit = useCallback(async () => {
    if (!draft) return;
    setError(null);
    setLoading(true);
    try {
      const result = await commitItemTypeFromJiraSample(spaceId, draft);
      message.success('Тип записи создан');
      onCreated(result.itemTypeId);
      reset();
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось создать тип');
    } finally {
      setLoading(false);
    }
  }, [draft, onCreated, reset, spaceId]);

  const updateField = useCallback(
    (jiraField: string, patch: Partial<DraftField>) => {
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              fields: prev.fields.map((f) =>
                f.jiraField === jiraField ? { ...f, ...patch } : f,
              ),
            }
          : prev,
      );
    },
    [],
  );

  const updateDict = useCallback(
    (refKey: string, patch: Partial<ProposedDictionary>) => {
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              proposedDictionaries: prev.proposedDictionaries.map((d) =>
                d.refKey === refKey ? { ...d, ...patch } : d,
              ),
            }
          : prev,
      );
    },
    [],
  );

  const toggleTitle = useCallback(
    (jiraField: string) => {
      setDraft((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          fields: prev.fields.map((f) =>
            f.jiraField === jiraField
              ? { ...f, isTitle: true, skip: false }
              : f.isTitle
                ? { ...f, isTitle: false }
                : f,
          ),
        };
      });
    },
    [],
  );

  return (
    <Modal
      open={open}
      title="Создать тип записи из примера Jira"
      width="92vw"
      style={{ top: 24, maxWidth: 1400 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      onCancel={handleCancel}
      footer={
        step === 'input' ? (
          <Space>
            <Button onClick={handleCancel}>Отмена</Button>
            <Button type="primary" loading={loading} onClick={handlePreview}>
              Получить превью
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              onClick={() => {
                setStep('input');
                setError(null);
              }}
            >
              Назад
            </Button>
            <Button onClick={handleCancel}>Отмена</Button>
            <Button type="primary" loading={loading} onClick={handleCommit}>
              Создать
            </Button>
          </Space>
        )
      }
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {step === 'input' ? (
        <InputStep
          value={issueKeysText}
          onChange={setIssueKeysText}
          loading={loading}
        />
      ) : draft ? (
        <EditStep
          draft={draft}
          setDraft={setDraft}
          updateField={updateField}
          updateDict={updateDict}
          toggleTitle={toggleTitle}
        />
      ) : null}
    </Modal>
  );
}

// ─── Шаг 1 ────────────────────────────────────────────────────────────────

export function InputStep({
  value,
  onChange,
  loading,
}: {
  value: string;
  onChange: (v: string) => void;
  loading: boolean;
}): JSX.Element {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Text type="secondary">
        Введите 1–20 ключей задач Jira (например, <Text code>GOALS-123</Text>).
        Каждый ключ — на новой строке либо через пробел/запятую.
      </Text>
      <TextArea
        rows={6}
        placeholder="GOALS-123&#10;GOALS-124"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        autoFocus
      />
      <Text type="secondary">
        Будут проанализированы поля задач и предложены атрибуты будущего типа
        записи. Перед созданием можно отредактировать имена, типы и список
        импортируемых полей.
      </Text>
    </Space>
  );
}

// ─── Шаг 2 ────────────────────────────────────────────────────────────────

export interface EditStepProps {
  draft: ItemTypeFromJiraSampleDraft;
  setDraft: (
    updater: (prev: ItemTypeFromJiraSampleDraft | null) => ItemTypeFromJiraSampleDraft | null,
  ) => void;
  updateField: (jiraField: string, patch: Partial<DraftField>) => void;
  updateDict: (refKey: string, patch: Partial<ProposedDictionary>) => void;
  toggleTitle: (jiraField: string) => void;
  /**
   * Если поле с таким slug уже существует в типе записи — переключатель
   * «Импорт.» заблокирован и в строке отображается тег «уже есть».
   * Используется в режиме «добавить поля к существующему типу».
   */
  lockedSlugs?: Set<string>;
  /** Заголовок «уже есть» — заголовок типа уже занят. */
  hideTitleColumn?: boolean;
}

export function EditStep({
  draft,
  setDraft,
  updateField,
  updateDict,
  toggleTitle,
  lockedSlugs,
  hideTitleColumn = false,
}: EditStepProps): JSX.Element {
  const fieldColumns: ColumnsType<DraftField> = useMemo(
    () => (([
      {
        title: 'Поле Jira',
        dataIndex: 'jiraField',
        width: 240,
        render: (_v, f) => {
          const locked = lockedSlugs?.has(f.suggestedSlug) ?? false;
          return (
            <div style={{ lineHeight: 1.3 }}>
              <Text style={{ fontSize: 13 }}>{f.jiraFieldName}</Text>
              {locked && (
                <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>
                  уже есть
                </Tag>
              )}
              <div>
                <Text code type="secondary" style={{ fontSize: 11 }}>
                  {f.jiraField}
                </Text>
              </div>
            </div>
          );
        },
      },
      {
        title: 'Имя в Lens',
        dataIndex: 'suggestedName',
        width: 200,
        render: (_v, f) => (
          <Input
            size="small"
            value={f.suggestedName}
            onChange={(e) => updateField(f.jiraField, { suggestedName: e.target.value })}
            disabled={f.skip}
          />
        ),
      },
      {
        title: 'Slug',
        dataIndex: 'suggestedSlug',
        width: 150,
        render: (_v, f) => (
          <Input
            size="small"
            value={f.suggestedSlug}
            onChange={(e) => updateField(f.jiraField, { suggestedSlug: e.target.value })}
            disabled={f.skip}
          />
        ),
      },
      {
        title: 'Тип',
        dataIndex: 'fieldType',
        width: 180,
        render: (_v, f) => (
          <Select<DraftFieldKind>
            size="small"
            value={f.fieldType}
            options={FIELD_KIND_OPTIONS}
            onChange={(v) =>
              updateField(f.jiraField, {
                fieldType: v,
                skip: v === 'skip' ? true : false,
              })
            }
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: 'Справочник',
        dataIndex: 'dictionaryRef',
        width: 160,
        render: (_v, f) =>
          f.fieldType === 'reference' && f.dictionaryRef ? (
            f.dictionaryRef.kind === 'proposed' ? (
              <Tag color="processing">новый: {f.dictionaryRef.refKey}</Tag>
            ) : (
              <Tag color="success">существующий</Tag>
            )
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Заголовок',
        width: 90,
        render: (_v, f) => (
          <Switch
            size="small"
            checked={f.isTitle}
            disabled={f.skip}
            onChange={() => toggleTitle(f.jiraField)}
          />
        ),
      },
      {
        title: 'Импорт.',
        width: 90,
        render: (_v, f) => {
          const locked = lockedSlugs?.has(f.suggestedSlug) ?? false;
          return (
            <Switch
              size="small"
              checked={!f.skip && !locked}
              disabled={locked}
              onChange={(checked) =>
                updateField(f.jiraField, {
                  skip: !checked,
                  isTitle: !checked ? false : f.isTitle,
                })
              }
            />
          );
        },
      },
      {
        title: 'Замечания',
        dataIndex: 'warnings',
        render: (_v, f) =>
          f.warnings.length > 0 ? (
            <Text type="warning" style={{ fontSize: 12 }}>
              {f.warnings.join('; ')}
            </Text>
          ) : null,
      },
    ] as ColumnsType<DraftField>).filter(
      (c) => !(hideTitleColumn && c.title === 'Заголовок'),
    )),
    [toggleTitle, updateField, lockedSlugs, hideTitleColumn],
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {draft.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Предупреждения"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {draft.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          }
        />
      )}

      <Form layout="vertical" size="small">
        <Space wrap style={{ width: '100%' }}>
          <Form.Item label="Имя типа" style={{ marginBottom: 0, minWidth: 240 }}>
            <Input
              value={draft.itemTypeName}
              onChange={(e) =>
                setDraft((prev) => (prev ? { ...prev, itemTypeName: e.target.value } : prev))
              }
            />
          </Form.Item>
          <Form.Item label="Slug типа" style={{ marginBottom: 0, minWidth: 200 }}>
            <Input
              value={draft.itemTypeSlug}
              onChange={(e) =>
                setDraft((prev) => (prev ? { ...prev, itemTypeSlug: e.target.value } : prev))
              }
            />
          </Form.Item>
          <Form.Item label="Jira project" style={{ marginBottom: 0 }}>
            <Input value={draft.jiraProjectKey} disabled style={{ width: 120 }} />
          </Form.Item>
          <Form.Item label="Issuetype" style={{ marginBottom: 0 }}>
            <Input value={draft.jiraIssueType} disabled style={{ width: 140 }} />
          </Form.Item>
        </Space>
      </Form>

      {draft.proposedDictionaries.length > 0 && (
        <Collapse
          defaultActiveKey={['dicts']}
          items={[
            {
              key: 'dicts',
              label: `Будут созданы новые справочники (${draft.proposedDictionaries.length})`,
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {draft.proposedDictionaries.map((d) => (
                    <Space key={d.refKey} wrap>
                      <Text code>{d.refKey}</Text>
                      <Form.Item label="Имя" style={{ marginBottom: 0 }}>
                        <Input
                          size="small"
                          value={d.name}
                          onChange={(e) => updateDict(d.refKey, { name: e.target.value })}
                          style={{ width: 220 }}
                        />
                      </Form.Item>
                      <Form.Item label="Ключевая колонка" style={{ marginBottom: 0 }}>
                        <Select
                          size="small"
                          value={d.keyColumn}
                          options={d.columns.map((c) => ({ value: c.slug, label: c.slug }))}
                          onChange={(v) => updateDict(d.refKey, { keyColumn: v })}
                          style={{ width: 180 }}
                        />
                      </Form.Item>
                      <Text type="secondary">
                        примеров записей: {d.sampleEntries.length}
                      </Text>
                    </Space>
                  ))}
                </Space>
              ),
            },
          ]}
        />
      )}

      <Table<DraftField>
        size="small"
        rowKey="jiraField"
        dataSource={draft.fields}
        columns={fieldColumns}
        pagination={false}
      />
    </Space>
  );
}
